import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import _ from 'lodash';
import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { useAppSelector } from 'src/store/store';
import { createSearchRegExp } from 'src/utils/searchUtils';
import { setRestrictionListItem } from 'src/store/newJmrPageSlice/newJmrPageSlice';

/**
 * Select a "RestrictionItem" from a dropdown list.
 */
const TaskSelector = (props: { listIndex: number }) => {
  const { listIndex } = props;
  const dispatch = useDispatch();
  const { restrictionListItems, consolidatedRegionTasks, isRestrictionsPageReadOnly } = useAppSelector(
    (state) => state.newJmrPage,
  );
  const task = restrictionListItems[listIndex];

  // State
  const [tasksQuery, setTasksQuery] = useState<string>();
  const [selectedTaskName, setSelectedTaskName] = useState<string | undefined>(task?.label);

  // Need to update the list when options in other blocks are picked. Don't exclude the
  // currently selected item though.
  const availableOptions = useMemo(() => {
    const selectedSet = new Set<string>(
      restrictionListItems
        .filter((i) => !!i && i?.label.toLowerCase() !== task?.label.toLowerCase())
        .map((i) => i?.label.toLowerCase() ?? ''),
    );
    const clonedTasks = _.cloneDeep(consolidatedRegionTasks);
    return clonedTasks.filter((t) => !selectedSet.has(t.label.toLowerCase()));
  }, [consolidatedRegionTasks, restrictionListItems, task?.label]);

  // Format task to be used in the menu
  const formattedTaskOptions = useMemo(() => {
    return availableOptions
      .map((t) => ({ label: t.label, value: t.label, rank: t.rank }))
      .sort((a, b) => (a.rank !== null ? a.rank : Infinity) - (b.rank != null ? b.rank : Infinity));
  }, [availableOptions]);

  // Apply user entered filter string
  const matchedTaskOptions = useMemo(() => {
    if (!tasksQuery) {
      return formattedTaskOptions;
    }
    const taskSearchRegExp = createSearchRegExp(tasksQuery);
    return formattedTaskOptions.filter((option) => !tasksQuery || taskSearchRegExp.test(option.label));
  }, [formattedTaskOptions, tasksQuery]);

  // If the list of options in redux changes, re-sync the selectedTaskName with the task for this index
  useEffect(() => {
    if (!!task && task.label !== selectedTaskName) {
      setSelectedTaskName(task.label);
    }
  }, [restrictionListItems.length]);

  // When the selection changes
  const onChange = (value: string) => {
    if (value !== selectedTaskName) {
      setSelectedTaskName(value);
      const task = availableOptions.find((t) => t.label === value);
      if (task) {
        dispatch(setRestrictionListItem({ item: task, index: listIndex }));
      }
    }
  };

  return (
    <Select
      data-cy={`new-jmr__restrictions__dropdown__${listIndex}`}
      data-testid={`new-jmr__restrictions__dropdown__${listIndex}`}
      searchQuery={tasksQuery}
      onSearch={(val) => setTasksQuery(val)}
      placeholder="Select task"
      value={selectedTaskName}
      onChange={onChange}
      disabled={isRestrictionsPageReadOnly}
      errorMessage={task.error}
      error={!!task.error}
    >
      {matchedTaskOptions.map((option) => (
        <SelectOption
          label={option.label}
          value={option.value}
          key={option.value}
          data-cy="new-jmr__restrictions__dropdown-item"
        />
      ))}
      {!matchedTaskOptions.length && (
        <Column alignmentVertical="center" spacing="300" spacingInset="500">
          <Text alignment="center">No tasks found</Text>
        </Column>
      )}
    </Select>
  );
};

export default TaskSelector;
